import { Effect, Layer, Ref, Result, Schema, Scope } from "effect";
import { Atom, AtomRegistry } from "effect/unstable/reactivity";
import { WorkspaceFiles } from "@workerstation/shared/station/workspace-input";
import {
  EditorDocumentHydration, EditorDocumentInputFailure, editorDocumentDependencyFailure,
  type EditorDocumentFailure, type EditorDocumentSnapshot, type EditorDocumentIdentity,
  type EditorFilesUpdate, type EditorFoldersUpdate
} from "./documents-model.ts";
import { makeDefaultFiles } from "../default-workspaces.ts";
import { EditorDocuments } from "./documents-service.ts";

const Folders = Schema.Array(Schema.String);

export const makeEditorDocuments = Effect.fn("Frontend.EditorDocuments.make")(function* (
  root: Scope.Scope, initialFiles: Readonly<Record<string, string>>
) {
  const owner = yield* Scope.Scope, registry = yield* AtomRegistry.AtomRegistry;
  const initial: EditorDocumentSnapshot = {
    workspaceId: null, generation: 0, hydrationSequence: 0, revision: 0,
    files: Object.freeze({ ...initialFiles }), folders: Object.freeze([]), closed: false
  };
  const state = yield* Ref.make(initial);
  const read = Ref.get(state), snapshot = Atom.make(read);
  const alive = () => owner.state._tag !== "Closed" && root.state._tag !== "Closed";
  yield* Effect.addFinalizer(() => Ref.update(state, value => ({ ...value, closed: true })));
  const publish = Effect.suspend(() => alive()
    ? Effect.try({ try: () => registry.refresh(snapshot), catch: editorDocumentDependencyFailure })
    : Effect.void);
  const matches = (value: EditorDocumentSnapshot, identity: EditorDocumentIdentity) =>
    alive() && !value.closed && value.workspaceId === identity.workspaceId && value.generation === identity.generation;

  const hydrate = Effect.fn("Frontend.EditorDocuments.hydrate")(function* (input: EditorDocumentHydration) {
    if (!alive()) return yield* Effect.interrupt;
    const decoded = yield* Schema.decodeUnknownEffect(EditorDocumentHydration)(input).pipe(
      Effect.mapError(() => new EditorDocumentInputFailure())
    );
    const value = yield* Ref.modify(state, (current): readonly [EditorDocumentSnapshot | undefined, EditorDocumentSnapshot] => {
      if (!alive() || current.closed || decoded.sequence <= current.hydrationSequence) return [undefined, current];
      const next: EditorDocumentSnapshot = {
        workspaceId: decoded.workspaceId, generation: current.generation + 1,
        hydrationSequence: decoded.sequence, revision: current.revision + 1,
        files: Object.freeze({ ...decoded.files }), folders: Object.freeze([...decoded.folders]), closed: false
      };
      return [next, next];
    });
    if (value) yield* publish;
    return value;
  });

  const updateFiles = Effect.fn("Frontend.EditorDocuments.updateFiles")(function* (identity: EditorDocumentIdentity, update: EditorFilesUpdate) {
    const prepared = yield* Ref.modify(state, (current): readonly [Effect.Effect<boolean, EditorDocumentFailure>, EditorDocumentSnapshot] => {
      if (!matches(current, identity)) return [Effect.succeed(false), current];
      const candidate = Result.try(() => typeof update === "function" ? update(current.files) : update);
      if (Result.isFailure(candidate)) return [Effect.fail(editorDocumentDependencyFailure(candidate.failure)), current];
      if (candidate.success === current.files) return [Effect.succeed(true), current];
      const decoded = Schema.decodeUnknownResult(WorkspaceFiles)(candidate.success);
      if (Result.isFailure(decoded)) return [Effect.fail(new EditorDocumentInputFailure()), current];
      return [Effect.succeed(true), { ...current, revision: current.revision + 1, files: Object.freeze({ ...decoded.success }) }];
    });
    const applied = yield* prepared;
    if (applied) yield* publish;
    return applied;
  });

  const updateFolders = Effect.fn("Frontend.EditorDocuments.updateFolders")(function* (identity: EditorDocumentIdentity, update: EditorFoldersUpdate) {
    const prepared = yield* Ref.modify(state, (current): readonly [Effect.Effect<boolean, EditorDocumentFailure>, EditorDocumentSnapshot] => {
      if (!matches(current, identity)) return [Effect.succeed(false), current];
      const candidate = Result.try(() => typeof update === "function" ? update(current.folders) : update);
      if (Result.isFailure(candidate)) return [Effect.fail(editorDocumentDependencyFailure(candidate.failure)), current];
      if (candidate.success === current.folders) return [Effect.succeed(true), current];
      const decoded = Schema.decodeUnknownResult(Folders)(candidate.success);
      if (Result.isFailure(decoded)) return [Effect.fail(new EditorDocumentInputFailure()), current];
      return [Effect.succeed(true), { ...current, revision: current.revision + 1, folders: Object.freeze([...decoded.success]) }];
    });
    const applied = yield* prepared;
    if (applied) yield* publish;
    return applied;
  });
  return EditorDocuments.of({ initial, read, snapshot, hydrate, updateFiles, updateFolders });
});

/** Initial draft acquisition is bootstrap: a refusal closes the partial root. */
export const EditorDocumentsLive = (
  root: Scope.Scope,
  initial: Effect.Effect<Readonly<Record<string, string>>, EditorDocumentFailure> = Effect.try({ try: makeDefaultFiles, catch: editorDocumentDependencyFailure })
) => Layer.effect(EditorDocuments, Effect.gen(function* () {
  const initialFiles = yield* initial.pipe(Effect.orDie);
  return yield* makeEditorDocuments(root, initialFiles);
}));
